import type { PackageOperationData } from '@openx/types/package/PackageHookTypes.ts';
import { useMutation } from '@tanstack/react-query';
import type { GraphQLFormattedError } from 'graphql/error';
import { useSnackbar } from 'notistack';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArchivePackageDocument } from '../../../graphql/mutation/packages/ArchivePackage.generated.tsx';
import { getGraphQLRequestFromClient } from '../../../modules/graphql/types.ts';
import { useLocalErrorHandling } from '../../../modules/graphql/useLocalErrorHandling.ts';

export const usePackageArchive = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { mutateAsync: archivePackageMutation } = useMutation({
    mutationFn: (id: string, { client }) => getGraphQLRequestFromClient(client)(ArchivePackageDocument, { id }),
  });

  const [isArchiving, setIsArchiving] = useState<boolean>(false);

  useLocalErrorHandling(ArchivePackageDocument, () => true);

  const archivePackage = useCallback(
    async (packages: PackageOperationData[]) => {
      setIsArchiving(true);
      const fulfilled: { id: string }[] = [];
      const errors: { graphQLErrors?: GraphQLFormattedError[]; packageId: string }[] = [];

      const results = await Promise.allSettled(packages.map(({ id }) => archivePackageMutation(id)));

      results.forEach((result, index) => {
        const { id } = packages[index];

        if (result.status === 'fulfilled' && result.value.archivePackage) {
          fulfilled.push({ id: result.value.archivePackage });
        } else if (result.status === 'rejected') {
          errors.push({ graphQLErrors: result.reason?.graphQLErrors, packageId: id });
        } else {
          //Case when error is not thrown api side but res.data.archivePackage is null for some reason
          errors.push({ packageId: id });
        }
      });

      const messages = {
        failDefault: (id: string) => t('Package {id} deletion failed.', { id }),
        successSummary: (allItemsNumber, successesNumber) => {
          if (allItemsNumber === 1) {
            return t('Successfully deleted package.');
          }

          return t('Successfully deleted {successesNumber} of {allItemsNumber} packages.', {
            allItemsNumber,
            successesNumber,
          });
        },
      };

      const failMessages = errors.map(error => {
        if (error.graphQLErrors?.[0]?.extensions?.errors?.[0]?.message) {
          return error.graphQLErrors[0].extensions.errors[0].message;
        } else {
          return messages.failDefault(error.packageId);
        }
      });

      const allItemsNumber = packages.length;
      const failsNumber = failMessages.length;
      const successNumber = fulfilled.length;

      if (failMessages.length) {
        failMessages.forEach(message => {
          enqueueSnackbar(message, { variant: 'error' });
        });
      }

      if (allItemsNumber !== failsNumber) {
        enqueueSnackbar(messages.successSummary(allItemsNumber, successNumber), { variant: 'success' });
      }

      setIsArchiving(false);

      return { errors, fulfilled };
    },
    [archivePackageMutation, enqueueSnackbar, t]
  );

  return { archivePackage, isArchiving };
};
